import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Paper,
  alpha,
} from "@mui/material";
import SportsTwoTone from "@mui/icons-material/SportsTwoTone";
import CategoryTwoTone from "@mui/icons-material/CategoryTwoTone";
import { useSchemes } from "../../../contexts/SchemesContext";
import { OffensiveRunConcept } from "../../../types/SchemesTypes";

export const RunConcepts = () => {
  const { runConcepts } = useSchemes();

  // Group concepts by play_concept_name
  const groupedConcepts = useMemo(() => {
    const groups: Record<string, OffensiveRunConcept[]> = {};

    runConcepts.forEach((concept) => {
      const groupName = concept.play_concept.name || "Uncategorized";
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(concept);
    });

    return groups;
  }, [runConcepts]);

  if (runConcepts.length === 0) {
    return (
      <Card
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 3,
          opacity: 0.8,
        }}
      >
        <CardContent>
          <Typography color="text.secondary" align="center">
            No run concepts found
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {Object.entries(groupedConcepts).map(([groupName, concepts]) => (
        <Box key={groupName} mb={4}>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              mb: 2,
              pl: 1,
              borderLeft: "3px solid #e91e63",
              display: "flex",
              alignItems: "center",
              opacity: 0.8,
            }}
          >
            <SportsTwoTone sx={{ mr: 1, fontSize: "1rem" }} />
            {groupName}
          </Typography>

          <Grid container spacing={2}>
            {concepts.map((runConcept) => (
              <Grid item xs={12} sm={6} md={4} key={runConcept.id}>
                <Card
                  sx={{
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    boxShadow: 3,
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                      cursor: "pointer",
                    },
                    background: "linear-gradient(to bottom, #1e1e1e, #2a2a2a)",
                  }}
                >
                  {/* Concept name in contrasting bar */}
                  <Box
                    sx={{
                      bgcolor: alpha("#e91e63", 0.15),
                      p: 1.5,
                      textAlign: "center",
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <Typography
                      variant="h5"
                      component="div"
                      fontWeight="500"
                      color="white"
                      sx={{
                        textShadow: "1px 1px 3px rgba(0,0,0,0.5)",
                      }}
                    >
                      {runConcept.name}
                    </Typography>
                  </Box>
                  {/* Header with school names */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 1.5,
                      bgcolor: alpha("#000", 0.4),
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      fontWeight="bold"
                      color="white"
                      sx={{
                        letterSpacing: 0.5,
                        textShadow: "0.5px 0.5px 2px rgba(0,0,0,0.5)",
                        fontFamily: '"Roboto Condensed", "Roboto", sans-serif',
                      }}
                    >
                      {runConcept.code_word_left}
                    </Typography>
                    <Typography
                      variant="h6"
                      component="div"
                      fontWeight="bold"
                      color="white"
                      sx={{
                        letterSpacing: 0.5,
                        textShadow: "0.5px 0.5px 2px rgba(0,0,0,0.5)",
                        fontFamily: '"Roboto Condensed", "Roboto", sans-serif',
                      }}
                    >
                      {runConcept.code_word_right}
                    </Typography>
                  </Box>

                  <CardContent
                    sx={{
                      p: 2,
                      backgroundImage:
                        "repeating-linear-gradient(0deg, transparent, transparent 19px, rgba(255,255,255,0.05) 19px, rgba(255,255,255,0.05) 20px)",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="#e91e63"
                        sx={{
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <SportsTwoTone fontSize="small" sx={{ mr: 0.5 }} />
                        Formations:
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        mb={2}
                      >
                        {runConcept.personnel_formations &&
                        runConcept.personnel_formations.length > 0 ? (
                          runConcept.personnel_formations.map(
                            (formation, index) => (
                              <Chip
                                key={index}
                                label={formation.formation.name}
                                size="medium"
                                sx={{
                                  mb: 0.5,
                                  fontSize: "0.85rem",
                                  maxWidth: "100%",
                                  height: "auto",
                                  borderRadius: "12px",
                                  bgcolor: alpha("#fff", 0.1),
                                  "& .MuiChip-label": {
                                    padding: "4px 8px",
                                    whiteSpace: "normal",
                                  },
                                }}
                              />
                            )
                          )
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </Stack>
                      {/* Personnel section */}
                      <Typography
                        variant="subtitle2"
                        color="#e91e63"
                        sx={{
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Personnel:
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        mb={2}
                      >
                        {runConcept.personnel_formations && runConcept.personnel_formations.length > 0 ? (
                          Array.from(
                            new Set(runConcept.personnel_formations.map(personnel => personnel.personnel_group.name))
                          ).map((uniqueName, index) => (
                            <Chip
                              key={index}
                              label={uniqueName}
                              size="medium"
                              sx={{
                                mb: 0.5,
                                fontSize: "0.85rem",
                                maxWidth: "100%",
                                height: "auto",
                                borderRadius: "12px",
                                bgcolor: alpha("#fff", 0.1),
                                "& .MuiChip-label": {
                                  padding: "4px 8px",
                                  whiteSpace: "normal",
                                },
                              }}
                            />
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </Stack>

                      <Divider sx={{ my: 1.5, opacity: 0.3 }} />

                      <Typography
                        variant="subtitle2"
                        color="#e91e63"
                        sx={{
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <CategoryTwoTone fontSize="small" sx={{ mr: 0.5 }} />
                        Tags:
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {runConcept.play_tags &&
                        runConcept.play_tags.length > 0 ? (
                          runConcept.play_tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag.name}
                              size="medium"
                              sx={{
                                mb: 0.5,
                                fontSize: "0.85rem",
                                maxWidth: "100%",
                                height: "auto",
                                borderRadius: "12px",
                                bgcolor: alpha("#e91e63", 0.15),
                                "& .MuiChip-label": {
                                  padding: "4px 8px",
                                  whiteSpace: "normal",
                                },
                              }}
                            />
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
